import type { CategoryId, ItemId } from './ids';
import type { Item } from './item';
import { cpiPlus, fixedRate } from './rate';
import { constant } from './scheduled';
import { TAX_PRESETS, type TaxPresetContext } from './tax-presets';
import type { MonthIndex, YearMonth } from './time';

/**
 * Item templates: data only. A template picks a primitive and sensible defaults; the UI supplies
 * the id, the (localised) name and usually a category. The engine never reads `template`.
 */
export const TEMPLATE_GROUPS = [
  'income',
  'expense',
  'event',
  'asset',
  'liability',
  'rule',
] as const;
export type TemplateGroup = (typeof TEMPLATE_GROUPS)[number];

export interface TemplateArgs {
  id: ItemId;
  name: string;
  category?: CategoryId;
  start?: MonthIndex;
  /** Project timing and labels for templates built from a tax preset. */
  taxContext?: TaxPresetContext;
}

export interface TemplateDef {
  group: TemplateGroup;
  create: (args: TemplateArgs) => Item;
}

const M0 = 0 as MonthIndex;

/** Without project timing, presets use the rules in force from their latest change. */
const DEFAULT_TAX_CONTEXT: TaxPresetContext = {
  start: '2026-08' as YearMonth,
  horizonMonths: 1,
  contributionsName: 'Social contributions',
};

const withCategory = (category: CategoryId | undefined): { category?: CategoryId } =>
  category === undefined ? {} : { category };

function recurring(
  template: string,
  direction: 'in' | 'out',
  amount: number,
  timing: 'monthly' | 'annual',
): TemplateDef['create'] {
  return ({ id, name, category, start = M0 }) => ({
    id,
    name,
    template,
    type: 'flow',
    direction,
    amount: constant(amount),
    amountBasis: 'today',
    frequency: 'monthly',
    start,
    growth: { kind: 'indexed', timing },
    ...withCategory(category),
  });
}

function oneOff(template: string, direction: 'in' | 'out', amount: number): TemplateDef['create'] {
  return ({ id, name, category, start = 12 as MonthIndex }) => ({
    id,
    name,
    template,
    type: 'flow',
    direction,
    amount: constant(amount),
    amountBasis: 'today',
    frequency: 'once',
    start,
    growth: { kind: 'indexed', timing: 'monthly' },
    ...withCategory(category),
  });
}

export const TEMPLATES = {
  salary: { group: 'income', create: recurring('salary', 'in', 3000, 'annual') },
  rentReceived: { group: 'income', create: recurring('rentReceived', 'in', 600, 'annual') },
  rentPaid: { group: 'expense', create: recurring('rentPaid', 'out', 900, 'annual') },
  utilities: { group: 'expense', create: recurring('utilities', 'out', 150, 'monthly') },
  livingCosts: { group: 'expense', create: recurring('livingCosts', 'out', 1200, 'monthly') },
  subscription: {
    group: 'expense',
    create: ({ id, name, category, start = M0 }) => ({
      id,
      name,
      template: 'subscription',
      type: 'flow',
      direction: 'out',
      amount: constant(15),
      amountBasis: 'today',
      frequency: 'monthly',
      start,
      growth: { kind: 'none' },
      ...withCategory(category),
    }),
  },
  bonus: { group: 'event', create: oneOff('bonus', 'in', 5000) },
  purchase: { group: 'event', create: oneOff('purchase', 'out', 20000) },
  inheritance: { group: 'event', create: oneOff('inheritance', 'in', 50000) },
  savings: {
    group: 'asset',
    create: ({ id, name, start = M0 }) => ({
      id,
      name,
      template: 'savings',
      type: 'asset',
      openingBalance: 10000,
      start,
      fundedFromCash: false,
      return: constant(fixedRate(0.02)),
    }),
  },
  investment: {
    group: 'asset',
    create: ({ id, name, start = M0 }) => ({
      id,
      name,
      template: 'investment',
      type: 'asset',
      openingBalance: 10000,
      start,
      fundedFromCash: false,
      return: constant({ kind: 'distribution', mean: 0.06, volatility: 0.15 }),
    }),
  },
  property: {
    group: 'asset',
    create: ({ id, name, start = M0 }) => ({
      id,
      name,
      template: 'property',
      type: 'asset',
      openingBalance: 250000,
      start,
      fundedFromCash: true,
      return: constant(cpiPlus(0.01)),
    }),
  },
  mortgage: {
    group: 'liability',
    create: ({ id, name, category, start = M0 }) => ({
      id,
      name,
      template: 'mortgage',
      type: 'loan',
      principal: 200000,
      start,
      termMonths: 300,
      rate: constant(fixedRate(0.035)),
      amortization: 'annuity',
      disburseToCash: true,
      earlyRepayments: [],
      ...withCategory(category),
    }),
  },
  consumerLoan: {
    group: 'liability',
    create: ({ id, name, category, start = M0 }) => ({
      id,
      name,
      template: 'consumerLoan',
      type: 'loan',
      principal: 15000,
      start,
      termMonths: 60,
      rate: constant(fixedRate(0.08)),
      amortization: 'annuity',
      disburseToCash: true,
      earlyRepayments: [],
      ...withCategory(category),
    }),
  },
  incomeTax: {
    group: 'rule',
    create: ({ id, name, category, start = M0 }) => ({
      id,
      name,
      template: 'incomeTax',
      type: 'formula',
      direction: 'out',
      spec: {
        kind: 'tax',
        sources: [{ ref: { type: 'totalIncome' }, sign: 1 }],
        deductions: [],
        brackets: constant([{ from: 0, rate: 0.1 }]),
        indexThresholds: 'none',
        settlement: 'monthly',
      },
      start,
      ...withCategory(category),
    }),
  },
  /** Bulgarian payroll: employee contributions + 10 % flat tax (docs/tax/bulgaria.md). */
  salaryTaxBg: {
    group: 'rule',
    create: ({ id, name, category, start = M0, taxContext = DEFAULT_TAX_CONTEXT }) => ({
      id,
      name,
      template: 'salaryTaxBg',
      type: 'formula',
      direction: 'out',
      spec: {
        ...TAX_PRESETS['bg-employment-2026'].build(taxContext),
        sources: [{ ref: { type: 'totalIncome' }, sign: 1 }],
      },
      start,
      ...withCategory(category),
    }),
  },
  savingsRule: {
    group: 'rule',
    create: ({ id, name, category, start = M0 }) => ({
      id,
      name,
      template: 'savingsRule',
      type: 'formula',
      direction: 'out',
      spec: {
        kind: 'percentOf',
        sources: [{ ref: { type: 'totalIncome' }, sign: 1 }],
        percent: constant(0.2),
      },
      start,
      ...withCategory(category),
    }),
  },
} as const satisfies Record<string, TemplateDef>;

export type TemplateId = keyof typeof TEMPLATES;
